import { useQueries, useQuery } from '@tanstack/react-query';
import { QueryKeys } from '@app/constants/queryKeys';
import { Chain } from '@thirdweb-dev/chains';
import { getContract, useAddress } from '@thirdweb-dev/react';
import erc20Abi from '../../../abis/erc20.json';
import { useSupportedChain } from '@app/hooks/other/useSupportedChain';
import { Token } from '@app/types/token';
import { formatUnits } from 'ethers/lib/utils';

async function fetcher(
  network: Chain | undefined,
  token: Token | undefined,
  spenderAddress: string | undefined,
  address: string | undefined
) {
  if (!network || !token || !spenderAddress || !address) {
    return null;
  }

  const contract = await getContract({
    address: token.contractAddress,
    network,
    contractTypeOrAbi: erc20Abi
  });

  // @ts-ignore
  const res = await contract.call('allowance', [address, spenderAddress]);

  return {
    value: res,
    formattedValue: formatUnits(res, token.decimals),
    contractAddress: token.contractAddress
  };
}

type AllowanceParams = {
  token: Token | undefined;
  spenderAddress: string | undefined;
};

export function useAllowance({ token, spenderAddress }: AllowanceParams) {
  const chain = useSupportedChain();
  const address = useAddress();

  return useQuery(
    [
      QueryKeys.TOKEN_ALLOWANCE,
      address,
      token?.contractAddress,
      spenderAddress,
      chain?.chainId
    ],
    () => fetcher(chain, token, spenderAddress, address),
    {
      enabled: Boolean(chain && token && spenderAddress && address)
    }
  );
}

type AllowancesParams = {
  tokens: Token[];
  spenderAddress: string | undefined;
};

export function useAllowances({ tokens, spenderAddress }: AllowancesParams) {
  const chain = useSupportedChain();
  const address = useAddress();

  return useQueries({
    queries: tokens.map(token => ({
      queryKey: [
        QueryKeys.BULK_TOKEN_ALLOWANCE,
        address,
        token?.contractAddress,
        spenderAddress,
        chain?.chainId
      ],
      queryFn: () => fetcher(chain, token, spenderAddress, address),
      enabled: Boolean(chain && token && spenderAddress && address)
    }))
  });
}
